import { oids } from "@/configs/Settings";

type ChangeKind = "changed" | "added" | "same";

export type OrganizationChangeItem = {
  key: string;
  label: string;
  kind: ChangeKind;
  before?: string;
  after?: string;
};

type Identifier = { system?: string; value?: string };

function safeString(value: unknown): string {
  if (value === null || value === undefined) return "";
  return String(value);
}

function joinNonEmpty(parts: string[], sep = " | "): string {
  return parts.map((p) => p.trim()).filter(Boolean).join(sep);
}

function findIdentifierValue(identifiers: unknown, system: string): string {
  if (!Array.isArray(identifiers)) return "";
  const match = (identifiers as Identifier[]).find((i) => i?.system === system);
  return match?.value ? safeString(match.value) : "";
}

function firstTypeCoding(org: fhir5.Organization | undefined | null) {
  const coding = org?.type?.[0]?.coding?.[0];
  return {
    code: safeString(coding?.code),
    display: safeString(coding?.display),
  };
}

export function summarizeOrganizationChanges(params: {
  before: fhir5.Organization;
  after: fhir5.Organization;
}): OrganizationChangeItem[] {
  const { before, after } = params;

  const cpfSystem = `urn:oid:${oids.cpf}`;
  const cnpjSystem = `urn:oid:${oids.cnpj}`;

  const beforeCpf = findIdentifierValue(before?.identifier, cpfSystem);
  const afterCpf = findIdentifierValue(after?.identifier, cpfSystem);
  const beforeCnpj = findIdentifierValue(before?.identifier, cnpjSystem);
  const afterCnpj = findIdentifierValue(after?.identifier, cnpjSystem);

  const beforeType = firstTypeCoding(before);
  const afterType = firstTypeCoding(after);

  const items: OrganizationChangeItem[] = [];

  const typeBefore = `${beforeType.code}${beforeType.display ? ` - ${beforeType.display}` : ""}`;
  const typeAfter = `${afterType.code}${afterType.display ? ` - ${afterType.display}` : ""}`;
  items.push({
    key: "type",
    label: "Tipo da organização",
    kind: typeBefore && typeAfter && typeBefore !== typeAfter ? "changed" : "same",
    before: typeBefore,
    after: typeAfter,
  });

  // Identifier (CNPJ)
  items.push({
    key: "identifier_cnpj",
    label: "Identificador (CNPJ)",
    kind: !beforeCnpj && !!afterCnpj ? "added" : beforeCnpj !== afterCnpj ? "changed" : "same",
    before: beforeCnpj,
    after: afterCnpj,
  });

  // Identifier (CPF) - idealmente deixa de existir; deixamos explícito no review quando houver remoção/alteração.
  const cpfKind: ChangeKind =
    !!beforeCpf && !afterCpf ? "changed" : beforeCpf !== afterCpf ? "changed" : "same";
  items.push({
    key: "identifier_cpf",
    label: "Identificador (CPF)",
    kind: cpfKind,
    before: beforeCpf,
    after: afterCpf,
  });

  // Qualification (natureza jurídica / CNAEs) - comum vir apenas no Validate alt.
  const beforeQualCount = Array.isArray(before?.qualification)
    ? before.qualification.length
    : 0;
  const afterQualCount = Array.isArray(after?.qualification)
    ? after.qualification.length
    : 0;
  items.push({
    key: "qualification",
    label: "Qualificações (Natureza Jurídica / CNAEs)",
    kind:
      beforeQualCount === 0 && afterQualCount > 0
        ? "added"
        : beforeQualCount !== afterQualCount
          ? "changed"
          : "same",
    before: beforeQualCount ? String(beforeQualCount) : "",
    after: afterQualCount ? String(afterQualCount) : "",
  });

  // Contact (telefone/email/endereço) - exibimos como resumo simples.
  const beforeTelecom = joinNonEmpty(
    (before?.contact ?? [])
      .flatMap((c: any) => (Array.isArray(c?.telecom) ? c.telecom : []))
      .map((t: any) => `${safeString(t?.system)}:${safeString(t?.value)}`),
  );
  const afterTelecom = joinNonEmpty(
    (after?.contact ?? [])
      .flatMap((c: any) => (Array.isArray(c?.telecom) ? c.telecom : []))
      .map((t: any) => `${safeString(t?.system)}:${safeString(t?.value)}`),
  );
  items.push({
    key: "contact_telecom",
    label: "Contato (telecom)",
    kind:
      !beforeTelecom && !!afterTelecom
        ? "added"
        : beforeTelecom !== afterTelecom
          ? "changed"
          : "same",
    before: beforeTelecom,
    after: afterTelecom,
  });

  const beforeAlias = Array.isArray(before?.alias) ? joinNonEmpty(before.alias as any[], ", ") : "";
  const afterAlias = Array.isArray(after?.alias) ? joinNonEmpty(after.alias as any[], ", ") : "";
  items.push({
    key: "alias",
    label: "Nome fantasia (alias)",
    kind:
      !beforeAlias && !!afterAlias
        ? "added"
        : beforeAlias !== afterAlias
          ? "changed"
          : "same",
    before: beforeAlias,
    after: afterAlias,
  });

  return items;
}

